'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import ColorPicker from '../ColorPicker';
import FilterBuilder from '../FilterBuilder';
import AnnotationList from '../AnnotationModal';
import s from './SeriesConfigurator.module.scss';

const MIXED_SERIES_TYPES = [
    { value: 'line', label: 'Line' },
    { value: 'column', label: 'Column' },
];

const SortableSeriesItem = ({
    series,
    index,
    isExpanded,
    onToggleExpand,
    onUpdate,
    onRemove,
    onDuplicate,
    canRemove,
    datasetFields,
    filterableFields,
    currentDataset,
    loadingFields,
    usedColors,
    validationErrors,
    fetchColumnValues,
    chartType,
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: series.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const handleFieldChange = (field, value) => {
        onUpdate(series.id, {
            serie: { ...series.serie, [field]: value },
        });
    };

    const handleZoneChange = (zoneField, value) => {
        const currentZone = series.serie?.zone || {
            axis: '',
            startValue: '',
            endValue: '',
        };
        onUpdate(series.id, {
            serie: {
                ...series.serie,
                zone: { ...currentZone, [zoneField]: value },
            },
        });
    };

    const clearZone = () => {
        onUpdate(series.id, {
            serie: {
                ...series.serie,
                zone: { axis: '', startValue: '', endValue: '' },
            },
        });
    };

    const handleFilterChange = (newFilters) => {
        onUpdate(series.id, { filterParams: newFilters });
    };

    const handleAddAnnotation = (annotation) => {
        const currentAnnotations = series.annotations || [];
        onUpdate(series.id, {
            annotations: [...currentAnnotations, annotation],
        });
    };

    const handleEditAnnotation = (index, annotation) => {
        const currentAnnotations = [...(series.annotations || [])];
        currentAnnotations[index] = annotation;
        onUpdate(series.id, { annotations: currentAnnotations });
    };

    const handleRemoveAnnotation = (index) => {
        const currentAnnotations = [...(series.annotations || [])];
        currentAnnotations.splice(index, 1);
        onUpdate(series.id, { annotations: currentAnnotations });
    };

    const errorPrefix = `series.${index}`;
    const zone = series.serie?.zone || {
        axis: '',
        startValue: '',
        endValue: '',
    };

    return (
        <div ref={setNodeRef} style={style} className={s.SeriesItem}>
            <div className={s.SeriesHeader}>
                <button
                    type="button"
                    className={s.DragHandle}
                    {...attributes}
                    {...listeners}>
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor">
                        <path d="M8 6h2v2H8V6zm6 0h2v2h-2V6zM8 11h2v2H8v-2zm6 0h2v2h-2v-2zm-6 5h2v2H8v-2zm6 0h2v2h-2v-2z" />
                    </svg>
                </button>

                <div
                    className={s.ColorDot}
                    style={{
                        backgroundColor: series.serie?.color || '#4FA0FF',
                    }}
                />

                <button
                    type="button"
                    className={s.SeriesName}
                    onClick={() => onToggleExpand(series.id)}>
                    <span>{series.serie?.label || `Series ${index + 1}`}</span>
                    <svg
                        className={`${s.ExpandIcon} ${isExpanded ? s.Expanded : ''}`}
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2">
                        <path d="M6 9l6 6 6-6" />
                    </svg>
                </button>

                {chartType !== 'heatmap' && (
                    <button
                        type="button"
                        className={s.DuplicateButton}
                        onClick={() => onDuplicate(series.id)}
                        title="Duplicate series">
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2">
                            <rect
                                x="9"
                                y="9"
                                width="13"
                                height="13"
                                rx="2"
                                ry="2"
                            />
                            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                        </svg>
                    </button>
                )}

                {canRemove && (
                    <button
                        type="button"
                        className={s.RemoveButton}
                        onClick={() => onRemove(series.id)}
                        title="Remove series">
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </button>
                )}
            </div>

            {isExpanded && (
                <div className={s.SeriesContent}>
                    <div className={s.FieldRow}>
                        <div className={s.Field}>
                            <label className={s.Label}>Label</label>
                            <input
                                type="text"
                                className={`${s.Input} ${validationErrors[`${errorPrefix}.label`] ? s.Invalid : ''}`}
                                value={series.serie?.label || ''}
                                onChange={(e) =>
                                    handleFieldChange('label', e.target.value)
                                }
                                placeholder="Series label"
                            />
                        </div>

                        <div className={s.Field}>
                            <label className={s.Label}>Value Property</label>
                            <select
                                className={`${s.Select} ${validationErrors[`${errorPrefix}.property`] ? s.Invalid : ''}`}
                                value={series.serie?.property || ''}
                                onChange={(e) =>
                                    handleFieldChange(
                                        'property',
                                        e.target.value
                                    )
                                }
                                disabled={loadingFields}>
                                <option value="">
                                    {loadingFields
                                        ? 'Loading...'
                                        : 'Select property'}
                                </option>
                                {datasetFields.map((field) => (
                                    <option key={field} value={field}>
                                        {field}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className={s.Field}>
                            <label className={s.Label}>X-Axis Category</label>
                            <select
                                className={`${s.Select} ${validationErrors[`${errorPrefix}.xAxisCategoryProperty`] ? s.Invalid : ''}`}
                                value={
                                    series.serie?.xAxisCategoryProperty || ''
                                }
                                onChange={(e) =>
                                    handleFieldChange(
                                        'xAxisCategoryProperty',
                                        e.target.value
                                    )
                                }
                                disabled={loadingFields}>
                                <option value="">
                                    {loadingFields
                                        ? 'Loading...'
                                        : 'Select category'}
                                </option>
                                {datasetFields.map((field) => (
                                    <option key={field} value={field}>
                                        {field}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {chartType === 'heatmap' && (
                            <div className={s.Field}>
                                <label className={s.Label}>
                                    Y-Axis Category
                                </label>
                                <select
                                    className={`${s.Select} ${validationErrors[`${errorPrefix}.yAxisCategoryProperty`] ? s.Invalid : ''}`}
                                    value={
                                        series.serie?.yAxisCategoryProperty ||
                                        ''
                                    }
                                    onChange={(e) =>
                                        handleFieldChange(
                                            'yAxisCategoryProperty',
                                            e.target.value
                                        )
                                    }
                                    disabled={loadingFields}>
                                    <option value="">
                                        {loadingFields
                                            ? 'Loading...'
                                            : 'Select category'}
                                    </option>
                                    {datasetFields.map((field) => (
                                        <option key={field} value={field}>
                                            {field}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    <div className={s.FieldRowHalf}>
                        <div className={s.Field}>
                            <label className={s.Label}>Color</label>
                            <ColorPicker
                                value={series.serie?.color}
                                onChange={(color) =>
                                    handleFieldChange('color', color)
                                }
                                usedColors={usedColors}
                            />
                        </div>

                        {chartType !== 'heatmap' && (
                            <div className={s.Field}>
                                <label className={s.CheckboxLabel}>
                                    <input
                                        type="checkbox"
                                        checked={
                                            series.serie?.negateSerieValues ||
                                            false
                                        }
                                        onChange={(e) =>
                                            handleFieldChange(
                                                'negateSerieValues',
                                                e.target.checked
                                            )
                                        }
                                    />
                                    <span>Negate values</span>
                                </label>
                            </div>
                        )}
                    </div>

                    {chartType === 'mixed' && (
                        <div className={s.FieldRowHalf}>
                            <div className={s.Field}>
                                <label className={s.Label}>
                                    Series Chart Type
                                </label>
                                <select
                                    className={s.Select}
                                    value={series.seriesChartType || 'line'}
                                    onChange={(e) =>
                                        onUpdate(series.id, {
                                            seriesChartType: e.target.value,
                                        })
                                    }>
                                    {MIXED_SERIES_TYPES.map((opt) => (
                                        <option
                                            key={opt.value}
                                            value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    <div className={s.Divider} />

                    <div className={s.SectionLabel}>Filters</div>
                    <FilterBuilder
                        filters={series.filterParams || []}
                        filterableFields={filterableFields}
                        loadingFields={loadingFields}
                        fetchColumnValues={fetchColumnValues}
                        currentDataset={currentDataset}
                        onChange={handleFilterChange}
                    />

                    {chartType !== 'heatmap' && <div className={s.Divider} />}

                    {chartType !== 'heatmap' && (
                        <div className={s.CollapsibleSection}>
                            <label className={s.CollapsibleHeader}>
                                <input
                                    type="checkbox"
                                    checked={!!zone.axis}
                                    onChange={(e) => {
                                        if (!e.target.checked) {
                                            clearZone();
                                        } else {
                                            handleZoneChange('axis', 'x');
                                        }
                                    }}
                                />
                                <span>Forecast Zone</span>
                            </label>
                            {zone.axis && (
                                <div className={s.CollapsibleContent}>
                                    <div className={s.FieldRow}>
                                        <div className={s.Field}>
                                            <label className={s.Label}>
                                                Axis
                                            </label>
                                            <select
                                                className={s.Select}
                                                value={zone.axis || ''}
                                                onChange={(e) =>
                                                    handleZoneChange(
                                                        'axis',
                                                        e.target.value
                                                    )
                                                }>
                                                <option value="x">
                                                    X-Axis
                                                </option>
                                                <option value="y">
                                                    Y-Axis
                                                </option>
                                            </select>
                                        </div>
                                        <div className={s.Field}>
                                            <label className={s.Label}>
                                                Start Value
                                            </label>
                                            <input
                                                type="text"
                                                className={s.Input}
                                                value={zone.startValue || ''}
                                                onChange={(e) =>
                                                    handleZoneChange(
                                                        'startValue',
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="Zone start"
                                            />
                                        </div>
                                        <div className={s.Field}>
                                            <label className={s.Label}>
                                                End Value
                                            </label>
                                            <input
                                                type="text"
                                                className={s.Input}
                                                value={zone.endValue || ''}
                                                onChange={(e) =>
                                                    handleZoneChange(
                                                        'endValue',
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="Optional"
                                            />
                                        </div>
                                    </div>
                                    <p className={s.HelpText}>
                                        Data after the start value will render
                                        as dashed line (forecast).
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {chartType !== 'heatmap' && (
                        <>
                            <div className={s.Divider} />

                            <div className={s.SectionLabel}>Annotations</div>
                            <AnnotationList
                                annotations={series.annotations || []}
                                onAdd={handleAddAnnotation}
                                onEdit={handleEditAnnotation}
                                onRemove={handleRemoveAnnotation}
                            />
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

const MemoizedSortableSeriesItem = React.memo(SortableSeriesItem);

const SeriesConfigurator = ({
    series,
    onAddSeries,
    onRemoveSeries,
    onDuplicateSeries,
    onUpdateSeries,
    onReorderSeries,
    datasetFields,
    filterableFields,
    currentDataset,
    loadingFields,
    validationErrors = {},
    fetchColumnValues,
    chartType,
}) => {
    const [expandedIds, setExpandedIds] = useState(() => {
        // Expand first series by default
        return series.length > 0 ? [series[0].id] : [];
    });

    // Update expandedIds if series IDs change (e.g., after config restore)
    const seriesIds = series.map((s) => s.id).join(',');

    useEffect(() => {
        const currentIds = seriesIds.split(',').filter(Boolean);
        setExpandedIds((prev) => {
            const hasValidExpanded = prev.some((id) => currentIds.includes(id));
            if (!hasValidExpanded && currentIds.length > 0) {
                return [currentIds[0]];
            }
            return prev;
        });
    }, [seriesIds]);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const usedColors = useMemo(
        () => series.map((s) => s.serie?.color).filter(Boolean),
        [series]
    );

    const toggleExpand = useCallback((id) => {
        setExpandedIds((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    }, []);

    const handleDragEnd = useCallback(
        (event) => {
            const { active, over } = event;

            if (over && active.id !== over.id) {
                const oldIndex = series.findIndex((s) => s.id === active.id);
                const newIndex = series.findIndex((s) => s.id === over.id);
                onReorderSeries(arrayMove(series, oldIndex, newIndex));
            }
        },
        [onReorderSeries, series]
    );

    const handleRemove = useCallback(
        (id) => {
            if (series.length <= 1) return;
            if (window.confirm('Remove this series?')) {
                onRemoveSeries(id);
                setExpandedIds((prev) => prev.filter((i) => i !== id));
            }
        },
        [onRemoveSeries, series.length]
    );

    const handleDuplicate = useCallback(
        (id) => {
            const newId = onDuplicateSeries(id);
            if (newId) setExpandedIds([newId]);
        },
        [onDuplicateSeries]
    );

    const handleAdd = useCallback(() => {
        const newId = onAddSeries();
        if (newId) setExpandedIds([newId]);
    }, [onAddSeries]);

    return (
        <div className={s.Root}>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}>
                <SortableContext
                    items={series.map((s) => s.id)}
                    strategy={verticalListSortingStrategy}>
                    <div className={s.SeriesList}>
                        {series.map((seriesItem, index) => (
                            <MemoizedSortableSeriesItem
                                key={seriesItem.id}
                                series={seriesItem}
                                index={index}
                                isExpanded={expandedIds.includes(seriesItem.id)}
                                onToggleExpand={toggleExpand}
                                onUpdate={onUpdateSeries}
                                onRemove={handleRemove}
                                onDuplicate={handleDuplicate}
                                canRemove={series.length > 1}
                                datasetFields={datasetFields}
                                filterableFields={filterableFields}
                                currentDataset={currentDataset}
                                loadingFields={loadingFields}
                                usedColors={usedColors}
                                validationErrors={validationErrors}
                                fetchColumnValues={fetchColumnValues}
                                chartType={chartType}
                            />
                        ))}
                    </div>
                </SortableContext>
            </DndContext>

            {chartType !== 'heatmap' && (
                <button
                    type="button"
                    className={s.AddButton}
                    onClick={handleAdd}>
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2">
                        <path d="M12 5v14M5 12h14" />
                    </svg>
                    Add Series
                </button>
            )}
        </div>
    );
};

export default SeriesConfigurator;
